import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { Capacitor } from '@capacitor/core';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { trackEvent } from '@/common/analytics';
import { APP_BRIDGE_PATH, detectMobileOs, storeUrlFor, type MobileOs } from '@/common/appStores';
import { claudeTokens } from '@/styles/claudeTokens';

const DISMISS_KEY: string = 'mc_app_banner_dismissed_at';
const DISMISS_DAYS: number = 7;

const Bar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #eaf0ee;
    border-bottom: 1px solid #d5e2de;
    font-size: 13px;
    color: ${claudeTokens.color.text1};
`;

const AppIcon = styled.img`
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #fff;
    flex: 0 0 auto;
`;

const Text = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    line-height: 1.35;

    strong {
        display: block;
        font-size: 14px;
    }

    span {
        color: ${claudeTokens.color.text2};
        font-size: 12px;
    }
`;

const GetButton = styled.a`
    flex: 0 0 auto;
    background: #5a9b96;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    padding: 8px 14px;
    border-radius: 999px;
    text-decoration: none;
`;

/**
 * 닫기 — 글자 "×" 대신 SVG(UI 가이드 4.1). 보이는 아이콘 18px, 누르는 영역 44×44.
 * 위아래 -4px 로 바 높이(56)를 늘리지 않고, 좌우 음수 여백으로 아이콘 자리는 예전 글자 자리(받기에서 약 14px·오른쪽 끝에서 약 15px)에 둔다.
 */
const CloseButton = styled.button`
    flex: 0 0 auto;
    width: 44px;
    height: 44px;
    margin: -4px -10px -4px -8px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: #8a8a8a;
    cursor: pointer;

    svg {
        display: block;
        width: 18px;
        height: 18px;
    }
`;

function isDismissed(): boolean {
    try {
        const at: string | null = localStorage.getItem(DISMISS_KEY);
        if (!at) return false;
        return Date.now() - Number(at) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    } catch {
        return false;
    }
}

/**
 * 휴대폰 브라우저의 앱 설치 배너 — Android·iOS 모두(2026-10-01~).
 * iOS 는 예전에 Safari 의 Smart App Banner(index.html 의 apple-itunes-app 메타)에 맡겼는데, Safari 가 아닌 브라우저
 * (크롬·네이버·카카오톡 안 브라우저)나 한 번 닫은 사파리에서는 뜨지 않아 같은 배너를 직접 그린다. 둘이 겹치지 않게 메타는 뺐다.
 * 앱 안(Capacitor)·브릿지 페이지·PC·닫은 뒤 7일 동안은 보이지 않는다.
 */
const AppInstallBanner: React.FC = () => {
    const location = useLocation();
    const [os, setOs] = useState<MobileOs>('other');
    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
        if (Capacitor.isNativePlatform()) return;
        const detected: MobileOs = detectMobileOs();
        if (detected === 'other') return;
        if (location.pathname.startsWith(APP_BRIDGE_PATH)) return;
        setOs(detected);
        setVisible(!isDismissed());
    }, [location.pathname]);

    const storeUrl: string | null = storeUrlFor(os);
    if (!visible || !storeUrl) return null;

    const dismiss = (): void => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch { /* 저장 불가(프라이빗 모드)면 이번 세션만 숨김 */ }
        setVisible(false);
    };

    return (
        <Bar role="region" aria-label="명치사 앱 설치 안내">
            <AppIcon src="/favicon.svg" alt="" />
            <Text>
                <strong>명치사 앱</strong>
                <span>새 채용·브리핑 알림을 앱으로 받아보세요</span>
            </Text>
            <GetButton
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('APP_STORE_CLICK', { path: location.pathname, targetId: `${os}_banner` })}
            >
                받기
            </GetButton>
            <CloseButton type="button" aria-label="닫기" onClick={dismiss}><CloseRoundedIcon aria-hidden /></CloseButton>
        </Bar>
    );
};

export default AppInstallBanner;
